import * as React from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { Newspaper, HeartPulse } from "lucide-react";
import { getPosts, type GetPostsResult } from "@/lib/blog/getPosts";
import { ProgressCTA } from "@/components/home/ProgressCTA/ProgressCTA";
import { BlogCard } from "@/components/cards/blog/BlogCard";
import type { Post } from "@/lib/blog/getPost";
import { seo } from "@/utilities/seo";
import { BlogPagination } from "@/components/blog/BlogPagination";
import { PATHS } from "@/config/paths";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/_home/blog/page/$pageNumber/")({
  params: {
    parse: (params) => ({
      pageNumber: z.coerce.number().min(1).parse(params.pageNumber),
    }),
    stringify: ({ pageNumber }) => ({ pageNumber: `${pageNumber}` }),
  },
  loader: async ({ params }): Promise<GetPostsResult> => {
    const page = params.pageNumber;
    const result = await getPosts({ data: { page, limit: 10 } });

    // If page number is invalid (beyond available pages), throw not found
    if (page > result.totalPages && result.totalPages > 0) {
      throw notFound();
    }

    return result;
  },
  component: BlogPageComponent,
  head: ({ loaderData, params }) => {
    const { totalPages, currentPage, totalPosts } = loaderData;
    const pageNumber = params.pageNumber;

    return {
      meta: [
        { title: `Blog - Page ${pageNumber} - ProgressMade.ai` },
        ...seo({
          title: `Blog - Page ${pageNumber} - ProgressMade.ai`,
          description: `Browse our health and fitness blog articles. Page ${pageNumber} of ${totalPages} - ${totalPosts} total articles on wellness, goal setting, and healthy habits.`,
          keywords:
            "blog, health, fitness, wellness, planning, health habits, structure, streaks, 75 hard, Bryan Johnson",
          image: "https://progressmade.ai/meta-image.webp",
          canonical: `https://progressmade.ai/blog/page/${pageNumber}`,
        }),
      ],
    };
  },
});

function BlogPageComponent() {
  const paginationData = Route.useLoaderData();
  const { posts, totalPages, currentPage, hasNextPage, hasPrevPage } = paginationData;

  return (
    <>
      <div className="col-span-12 md:col-span-9 md:col-start-2 xl:col-span-8 xl:col-start-3">
        <div className="col-span-12 flex flex-col items-center gap-4 mb-6 md:mb-12 text-center">
          <HeartPulse className="h-8 w-8 text-gray-700" />
          <Heading level="h1" size="h1" className="flex gap-2">
            Blog
          </Heading>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
            Insights, tips, and stories to support your health journey.
          </p>

          {currentPage > 1 && (
            <p className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map((post: Post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </div>

        <BlogPagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPrevPage={hasPrevPage}
        />
      </div>

      <ProgressCTA />
    </>
  );
}
